'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
  Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft, ArrowRight,
  Shield, Wrench, Star, Users, Zap, Mail, Lock, User,
  Building2, Phone, MapPin, ChevronRight, X
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'register' | 'verify' | 'claim';

interface RegisterForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface VerifyForm {
  businessName: string;
  phone: string;
  postcode: string;
  tradeCategory: string;
}

// ─── Trade categories ─────────────────────────────────────────────────────────

const TRADE_CATEGORIES = [
  'Plumber', 'Electrician', 'Builder / General Builder', 'Carpenter / Joiner',
  'Painter & Decorator', 'Plasterer', 'Roofer', 'Tiler',
  'Glazier', 'Locksmith', 'Gas Engineer / Boiler', 'HVAC / Air Conditioning',
  'Landscaper / Gardener', 'Driveway / Paving', 'Flooring Specialist',
  'Kitchen Fitter', 'Bathroom Fitter', 'Handyman', 'Cleaner', 'Other',
];

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  const steps: { key: Step; label: string; icon: React.ElementType }[] = [
    { key: 'register', label: 'Create account', icon: User },
    { key: 'verify', label: 'Verify identity', icon: Shield },
    { key: 'claim', label: 'Access dashboard', icon: Zap },
  ];
  const idx = steps.findIndex((s) => s.key === current);

  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((step, i) => {
        const done = i < idx;
        const active = i === idx;
        const IconComp = step.icon;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                  done
                    ? 'bg-emerald-500 text-white'
                    : active
                    ? 'text-white' :'bg-gray-100 text-gray-400'
                }`}
                style={active ? { backgroundColor: '#3A2047' } : undefined}
              >
                {done ? <CheckCircle2 size={16} /> : <IconComp size={16} />}
              </div>
              <span
                className={`text-xs font-medium whitespace-nowrap ${
                  active ? 'text-love-plum-light' : done ? 'text-emerald-600' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`h-0.5 w-12 mb-5 mx-1 transition-all duration-300 ${
                  i < idx ? 'bg-emerald-400' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Social proof strip ───────────────────────────────────────────────────────

function SocialProof() {
  return (
    <div className="flex items-center justify-center gap-5 flex-wrap mt-6 mb-2">
      {[
        { icon: Users, text: '2,400+ trades registered' },
        { icon: Star, text: 'Free forever plan' },
        { icon: Shield, text: 'No card required' },
      ].map(({ icon: Icon, text }) => (
        <div key={text} className="flex items-center gap-1.5 text-xs text-gray-500">
          <Icon size={13} className="text-love-plum-light" />
          <span>{text}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Step 1: Register ─────────────────────────────────────────────────────────

function RegisterStep({
  onNext,
}: {
  onNext: (email: string) => void;
}) {
  const { signUp } = useAuth();
  const [form, setForm] = useState<RegisterForm>({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof RegisterForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim()) return setError('Please enter your full name.');
    if (!form.email.trim()) return setError('Please enter your email address.');
    if (form.password.length < 6) return setError('Password must be at least 6 characters.');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      await signUp(form.email, form.password, {
        fullName: form.fullName,
        accountType: 'provider',
      });
      onNext(form.email);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 font-display">Create your trade account</h2>
        <p className="text-sm text-gray-500 mt-1">Join thousands of tradespeople on LoveLocals</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl mb-4 text-sm bg-red-50 text-red-600">
          <X size={15} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Full name <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              autoComplete="name"
              value={form.fullName}
              onChange={set('fullName')}
              placeholder="Jane Smith"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Email address <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set('email')}
              placeholder="you@example.com"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Password <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={form.password}
              onChange={set('password')}
              placeholder="Min. 6 characters"
              className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Confirm password <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={set('confirmPassword')}
              placeholder="Repeat password"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 active:scale-95 disabled:opacity-60 mt-2"
          style={{ backgroundColor: '#3A2047' }}
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin" /> Creating account…</>
          ) : (
            <><ArrowRight size={16} /> Create my trade account</>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-gray-400 mt-5">
        Already have an account?{' '}
        <Link href="/login?type=provider" className="text-love-plum-light font-medium hover:underline">
          Log in
        </Link>
      </p>
    </div>
  );
}

// ─── Step 2: Verify identity ──────────────────────────────────────────────────

function VerifyStep({
  email,
  onNext,
}: {
  email: string;
  onNext: (data: VerifyForm) => void;
}) {
  const { signIn } = useAuth();
  const [form, setForm] = useState<VerifyForm>({
    businessName: '',
    phone: '',
    postcode: '',
    tradeCategory: '',
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const supabase = createClient();

  // Poll for email confirmation
  useEffect(() => {
    const interval = setInterval(async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user?.email_confirmed_at) {
        setEmailVerified(true);
        clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const set = (k: keyof VerifyForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.businessName.trim()) return setError('Please enter your business name.');
    if (!form.tradeCategory) return setError('Please select your trade category.');
    if (!form.postcode.trim()) return setError('Please enter your postcode.');

    setLoading(true);
    try {
      // Sign in to get session (email may or may not be confirmed yet)
      if (password) {
        try {
          await signIn(email, password);
        } catch {
          // ignore — user may not have confirmed yet, we'll still save details
        }
      }

      // Save business details to provider_profiles
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('provider_profiles').upsert({
          id: user.id,
          business_name: form.businessName,
          phone: form.phone || null,
          postcode: form.postcode.toUpperCase(),
          trade_category: form.tradeCategory,
          subscription_tier: 'free',
          profile_completion_pct: 30,
          job_swap_opted_in: false,
          service_radius_km: 15,
        }, { onConflict: 'id' });
      }

      onNext(form);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900 font-display">Verify your identity</h2>
        <p className="text-sm text-gray-500 mt-1">Tell us about your trade business</p>
      </div>

      {/* Email confirmation notice */}
      <div
        className={`flex items-start gap-3 p-3.5 rounded-xl mb-5 text-sm transition-all ${
          emailVerified
            ? 'bg-emerald-50 border border-emerald-200' :'bg-amber-50 border border-amber-200'
        }`}
      >
        {emailVerified ? (
          <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
        ) : (
          <Mail size={16} className="text-amber-500 shrink-0 mt-0.5" />
        )}
        <div>
          {emailVerified ? (
            <p className="text-emerald-700 font-medium">Email confirmed ✓</p>
          ) : (
            <>
              <p className="text-amber-700 font-medium">Check your inbox</p>
              <p className="text-amber-600 text-xs mt-0.5">
                We sent a confirmation link to <strong>{email}</strong>. You can fill in your details while you wait.
              </p>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl mb-4 text-sm bg-red-50 text-red-600">
          <X size={15} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Business name <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={form.businessName}
              onChange={set('businessName')}
              placeholder="Smith Plumbing Ltd"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Trade category <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Wrench size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={form.tradeCategory}
              onChange={set('tradeCategory')}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors appearance-none bg-white"
            >
              <option value="">Select your trade…</option>
              {TRADE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Postcode <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={form.postcode}
                onChange={set('postcode')}
                placeholder="SW1A 1AA"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors uppercase"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Phone <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="07700 900000"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Password re-entry to sign in */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Your password <span className="text-gray-400 font-normal">(to sign in)</span>
          </label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 active:scale-95 disabled:opacity-60 mt-2"
          style={{ backgroundColor: '#3A2047' }}
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin" /> Saving details…</>
          ) : (
            <><ChevronRight size={16} /> Continue to dashboard</>
          )}
        </button>
      </form>
    </div>
  );
}

// ─── Step 3: Claim / access dashboard ────────────────────────────────────────

function ClaimStep({ businessName }: { businessName: string }) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const handleGoToDashboard = () => {
    setLoading(true);
    router.push('/provider/dashboard');
  };

  return (
    <div className="text-center py-4">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
        style={{ background: 'linear-gradient(135deg, #250C31 0%, #3A2047 100%)' }}
      >
        <CheckCircle2 size={32} className="text-love-mint" />
      </div>

      <h2 className="text-xl font-bold text-gray-900 font-display mb-2">
        You're all set, {businessName || 'welcome'}!
      </h2>
      <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
        Your trade profile has been created. Head to your dashboard to complete your profile and start receiving job leads.
      </p>

      {/* What's next */}
      <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left space-y-3">
        {[
          { icon: User, text: 'Complete your profile to rank higher in search' },
          { icon: Zap, text: 'Browse and quote on local job posts' },
          { icon: Star, text: 'Collect reviews to build trust with homeowners' },
        ].map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#3A204718' }}>
              <Icon size={14} style={{ color: '#3A2047' }} />
            </div>
            <span className="text-sm text-gray-600">{text}</span>
          </div>
        ))}
      </div>

      <button
        onClick={handleGoToDashboard}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold text-love-plum transition-all duration-200 active:scale-95 disabled:opacity-60 mb-3"
        style={{ backgroundColor: '#99E6C8' }}
      >
        {loading ? (
          <><Loader2 size={16} className="animate-spin" /> Loading dashboard…</>
        ) : (
          <><Zap size={16} /> Go to my dashboard <ArrowRight size={15} /></>
        )}
      </button>

      <p className="text-xs text-gray-400">
        Want to claim an existing listing?{' '}
        <Link href="/provider/onboarding" className="text-love-plum-light hover:underline font-medium">
          Find your business listing
        </Link>
      </p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ProviderSignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const [step, setStep] = useState<Step>('register');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verifiedBusiness, setVerifiedBusiness] = useState('');

  // Redirect if already authenticated provider
  useEffect(() => {
    if (!authLoading && user && user.user_metadata?.account_type === 'provider') {
      router.replace('/provider/dashboard');
    }
  }, [user, authLoading, router]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-love-plum-light" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#F8F5FA' }}>
      <div className="w-full max-w-md">
        {/* Back link */}
        <Link
          href="/homepage"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft size={15} />
          Back to LoveLocals
        </Link>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3"
              style={{ background: 'linear-gradient(135deg, #250C31 0%, #3A2047 100%)' }}
            >
              <Wrench size={22} className="text-love-mint" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 font-display">Join as a Trade Pro</h1>
            <p className="text-sm text-gray-500 mt-1">Register, verify, and claim your profile</p>
          </div>

          {/* Step indicator */}
          <StepIndicator current={step} />

          {/* Step content */}
          {step === 'register' && (
            <RegisterStep
              onNext={(email) => {
                setRegisteredEmail(email);
                setStep('verify');
              }}
            />
          )}

          {step === 'verify' && (
            <VerifyStep
              email={registeredEmail}
              onNext={(data) => {
                setVerifiedBusiness(data.businessName);
                setStep('claim');
              }}
            />
          )}

          {step === 'claim' && (
            <ClaimStep businessName={verifiedBusiness} />
          )}
        </div>

        {/* Social proof */}
        {step === 'register' && <SocialProof />}

        <p className="text-center text-xs text-gray-400 mt-5">
          By continuing, you agree to our{' '}
          <span className="text-love-plum-light cursor-pointer hover:underline">Terms of Service</span>
          {' '}and{' '}
          <span className="text-love-plum-light cursor-pointer hover:underline">Privacy Policy</span>
        </p>
      </div>
    </div>
  );
}

export default function ProviderSignupPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F5FA' }}>
        <Loader2 size={24} className="animate-spin text-love-plum-light" />
      </div>
    }>
      <ProviderSignupContent />
    </Suspense>
  );
}
